import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAccessibility } from './useAccessibility';
import { AccessibilityStatement } from './AccessibilityStatement';
import { useSystemSettings } from "@/hooks/use-system-settings";
import { 
  Accessibility, X, Type, Contrast, Palette, Droplet, 
  Link as LinkIcon, ScanLine, AlignJustify, ImageOff, 
  MousePointer2, PauseCircle, RefreshCw, FileText 
} from 'lucide-react';
import { cn } from '@/lib/utils';

const ReadingMask = ({ active }: { active: boolean }) => {
  const [mouseY, setMouseY] = useState(0);

  useEffect(() => {
    if (!active) return;
    const updateMousePosition = (e: MouseEvent) => setMouseY(e.clientY);
    
    setMouseY(window.innerHeight / 2);
    window.addEventListener('mousemove', updateMousePosition);
    return () => window.removeEventListener('mousemove', updateMousePosition);
  }, [active]);

  if (!active) return null;
  const maskHeight = 140;

  return createPortal(
    <div 
      className="fixed inset-0 z-[99998] pointer-events-none transition-opacity duration-150"
      style={{
        background: `linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.65) ${mouseY - maskHeight/2}px, transparent ${mouseY - maskHeight/2}px, transparent ${mouseY + maskHeight/2}px, rgba(0,0,0,0.65) ${mouseY + maskHeight/2}px, rgba(0,0,0,0.65) 100%)`
      }}
    />,
    document.body
  );
};

const A11yToggle = ({ icon: Icon, label, checked, onChange }: any) => (
  <button 
    onClick={() => onChange(!checked)}
    className={cn(
      "w-full flex items-center justify-between p-3 rounded-xl border transition-all duration-200",
      checked ? "bg-blue-50 border-blue-200 shadow-sm" : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50"
    )}
    role="switch"
    aria-checked={checked}
    aria-label={label}
  >
    <span className="flex items-center gap-3 text-sm font-bold text-slate-700">
      <Icon className={cn("w-5 h-5", checked ? "text-blue-600" : "text-slate-400")} />
      {label}
    </span>
    <div className={cn("relative w-11 h-6 rounded-full transition-colors", checked ? "bg-blue-600" : "bg-slate-200")}>
      <div className={cn("absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-200 shadow-sm", checked ? "left-1" : "right-1")} />
    </div>
  </button>
);

export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  
  const { settings, updateSetting, resetSettings } = useAccessibility();
  const { data: sysSettings } = useSystemSettings();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || showStatement) return;
    
    const panel = panelRef.current;
    if (!panel) return;
    
    const focusableElements = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        return;
      }
      
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };
    
    panel.addEventListener('keydown', handleKeyDown);
    firstElement.focus();
    
    return () => panel.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, showStatement]);

  const handleToggle = (key: keyof typeof settings, value: boolean, label: string) => {
    updateSetting(key, value as never);
    setAnnouncement(`תכונת ${label} ${value ? 'הופעלה' : 'כובתה'}`);
  };

  const handleReset = () => {
    resetSettings();
    setAnnouncement("הגדרות הנגישות אופסו לברירת המחדל");
  };

  const displayFeatures = [
    { id: 'highContrast', icon: Contrast, label: 'ניגודיות גבוהה' },
    { id: 'invertColors', icon: Palette, label: 'היפוך צבעים' },
    { id: 'grayscale', icon: Droplet, label: 'גווני אפור' },
    { id: 'highlightLinks', icon: LinkIcon, label: 'הדגשת קישורים' },
  ];

  const contentFeatures = [
    { id: 'readingMask', icon: ScanLine, label: 'מסכת קריאה' },
    { id: 'dyslexiaFont', icon: Type, label: 'גופן קריא (דיסלקציה)' },
    { id: 'increasedSpacing', icon: AlignJustify, label: 'ריווח טקסט מוגדל' },
    { id: 'hideImages', icon: ImageOff, label: 'הסתרת תמונות' },
  ];

  const navFeatures = [
    { id: 'bigCursor', icon: MousePointer2, label: 'סמן עכבר מוגדל' },
    { id: 'stopAnimations', icon: PauseCircle, label: 'עצירת אנימציות' },
  ];

  if (sysSettings?.enable_accessibility_widget === 'false') return null;

  return createPortal(
    <>
      {/* ARIA Live Region for screen readers */}
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>

      {/* Dark Overlay (Backdrop) */}
      <div 
        className={cn(
          "fixed inset-0 z-[99998] bg-slate-900/20 backdrop-blur-sm transition-opacity duration-300",
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* The Side Drawer (Panel) */}
      <div 
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="תפריט נגישות"
        className={cn(
          "fixed top-0 left-0 h-full z-[99999] pointer-events-auto bg-white flex flex-col shadow-[10px_0_40px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-in-out",
          "w-[85vw] sm:w-[360px]",
          isOpen ? "translate-x-0" : "-translate-x-full" 
        )}
        dir="rtl"
      >
        {/* Header - Updated to slate-900 */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white shrink-0">
          <h2 className="font-bold text-lg flex items-center gap-2">
            <Accessibility className="w-5 h-5" />
            תפריט נגישות
          </h2>
          <button 
            onClick={() => setIsOpen(false)} 
            aria-label="סגור תפריט נגישות" 
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-5 overflow-y-auto flex-1 space-y-6 no-scrollbar bg-white">
          
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2">תצוגה וגודל טקסט</h3>
            <div className="flex gap-2">
              {['normal', 'large', 'xlarge'].map(size => (
                <button 
                  key={size}
                  onClick={() => {
                    updateSetting('fontSize', size as any);
                    setAnnouncement(`גודל טקסט שונה ל${size === 'normal' ? 'רגיל' : size === 'large' ? 'גדול' : 'ענק'}`);
                  }}
                  className={cn(
                    "flex-1 py-2.5 rounded-xl border font-bold transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
                    settings.fontSize === size 
                      ? "bg-slate-900 text-white border-slate-900 shadow-sm" 
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  )}
                  aria-pressed={settings.fontSize === size}
                >
                  {size === 'normal' ? 'רגיל' : size === 'large' ? 'גדול' : 'ענק'}
                </button>
              ))}
            </div>
            
            <div className="space-y-2.5 pt-2">
              {displayFeatures.map(f => (
                <A11yToggle 
                  key={f.id} 
                  icon={f.icon} 
                  label={f.label} 
                  checked={(settings as any)[f.id]} 
                  onChange={(val: boolean) => handleToggle(f.id as any, val, f.label)} 
                />
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2">תוכן וקריאות</h3>
            <div className="space-y-2.5">
              {contentFeatures.map(f => (
                <A11yToggle 
                  key={f.id} 
                  icon={f.icon} 
                  label={f.label} 
                  checked={(settings as any)[f.id]} 
                  onChange={(val: boolean) => handleToggle(f.id as any, val, f.label)} 
                />
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2">ניווט והתמצאות</h3>
            <div className="space-y-2.5">
              {navFeatures.map(f => (
                <A11yToggle 
                  key={f.id} 
                  icon={f.icon} 
                  label={f.label} 
                  checked={(settings as any)[f.id]} 
                  onChange={(val: boolean) => handleToggle(f.id as any, val, f.label)} 
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Footer Actions */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex gap-3 shrink-0">
          <button 
            onClick={handleReset} 
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            <RefreshCw className="w-4 h-4" />
            איפוס
          </button>
          <button 
            onClick={() => { setIsOpen(false); setShowStatement(true); }} 
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-slate-600"
          >
            <FileText className="w-4 h-4" />
            הצהרת
          </button>
        </div>
      </div>

      {/* The Floating Action Button (FAB) attached to the left edge - without pulse */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed bottom-48 md:bottom-24 left-0 z-[99999] pointer-events-auto",
          "h-12 w-12 bg-slate-900 text-white rounded-r-xl flex items-center justify-center",
          "shadow-lg shadow-slate-900/20 transition-all duration-300",
          "focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 hover:w-14",
          isOpen ? "-translate-x-full opacity-0" : "translate-x-0 opacity-100"
        )}
        aria-label="פתח תפריט נגישות"
        aria-expanded={isOpen}
      >
        <Accessibility className="w-6 h-6 shrink-0" />
      </button>

      <ReadingMask active={settings.readingMask} />
      <AccessibilityStatement open={showStatement} onOpenChange={setShowStatement} />
    </>,
    document.body
  );
};